import React, { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import { useCursorTrail } from "./hooks/useCursorTrail";
import { AlertProvider } from "./components/ui/AlertModal";
import { SLPProvider } from "./context/SLPContext";
import { PremiumProvider } from "./context/PremiumContext";
import { CreditProvider } from "./context/CreditContext";
import { StorageProvider } from "./context/StorageContext";
import { ThemeProvider } from "./context/ThemeContext";
import { sbQuery, supabase } from "./config/supabase";
import { setActiveUser } from "./lib/userStorage";
import Preloader from "./components/ui/Preloader";
import ErrorBoundary from "./components/ui/ErrorBoundary";

// Remove the static HTML shell preloader once React mounts
if (typeof document !== "undefined") {
  const shell = document.querySelector(".sl-preload");
  if (shell) shell.remove();
}

// Lazy-loaded routes — only downloaded when needed
const LandingPage      = lazy(() => import("./components/landing/LandingPage"));
const SignUpPage       = lazy(() => import("./components/auth/SignUpPage"));
const SignInPage       = lazy(() => import("./components/auth/SignInPage"));
const WelcomePage      = lazy(() => import("./components/auth/WelcomePage"));
const StudentDashboard = lazy(() => import("./components/dashboard/student/StudentDashboard"));
const TutorDashboard   = lazy(() => import("./components/dashboard/tutor/TutorDashboard"));
const AdminDashboard   = lazy(() => import("./components/dashboard/admin/AdminDashboard"));
const ParentDashboard  = lazy(() => import("./components/dashboard/parent/ParentDashboard"));

function roleToView(role) {
  if (role === "tutor")  return "tutor-dash";
  if (role === "admin")  return "admin-dash";
  if (role === "parent") return "parent-dash";
  return "student-dash";
}

export default function App() {
  return (
    <ThemeProvider>
    <AlertProvider>
      <SLPProvider>
      <PremiumProvider>
      <CreditProvider>
      <StorageProvider>
        <ErrorBoundary>
          <AppInner />
        </ErrorBoundary>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "linear-gradient(145deg, rgba(24,24,27,.98), rgba(18,18,20,.98))",
              color: "#f4f4f5",
              border: "1px solid rgba(255,255,255,.08)",
              borderRadius: 14,
              fontSize: 13,
              fontFamily: "'DM Sans',sans-serif",
              backdropFilter: "blur(12px)",
              boxShadow: "0 12px 40px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.04)",
            },
            success: { iconTheme: { primary: "#22c55e", secondary: "#fff" } },
            error:   { iconTheme: { primary: "#ef4444", secondary: "#fff" } },
            duration: 3000,
          }}
        />
      </StorageProvider>
      </CreditProvider>
      </PremiumProvider>
      </SLPProvider>
    </AlertProvider>
    </ThemeProvider>
  );
}

// Clear ephemeral session data (user-specific data is now keyed by userId and preserved)
function clearUserStorage() {
  try {
    sessionStorage.clear();
    setActiveUser(null);
  } catch {}
}

const PAGE_TRANSITION = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.18 } },
};

function AppInner() {
  const [view,          setView]          = useState("landing");
  const [session,       setSession]       = useState(null);
  const [welcomeData,   setWelcomeData]   = useState(null); // { name, role, session }
  const [initializing,  setInitializing]  = useState(true);
  const [showPreloader, setShowPreloader] = useState(true);
  const [refCode,       setRefCode]       = useState("");   // ?ref= from URL
  const CursorTrailLayer = useCursorTrail({ color: "#818cf8" });

  // Fade out preloader once app initializes
  useEffect(() => {
    if (!initializing && showPreloader) {
      const t = setTimeout(() => setShowPreloader(false), 600);
      return () => clearTimeout(t);
    }
  }, [initializing, showPreloader]);

  // Handle ?ref= referral code in URL — navigate to signup
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) { setRefCode(ref); setView("signup"); }
    } catch {}
  }, []);

  // Supabase auth listener — restore session on page refresh
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (s) {
        setActiveUser(s.user.id);
        setSession(s);
        fetchRole(s.access_token, s.user.id)
          .then(role => setView(roleToView(role)))
          .finally(() => setInitializing(false));
      } else {
        setInitializing(false);
      }
    }).catch(() => setInitializing(false));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (s) { setActiveUser(s.user.id); setSession(s); }
      if (_event === "SIGNED_OUT") { clearUserStorage(); setSession(null); setView("landing"); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchRole = async (accessToken, userId) => {
    try {
      const rows = await sbQuery("profiles", accessToken, "GET", null, `?id=eq.${userId}&select=role`);
      return rows?.[0]?.role || "student";
    } catch {
      return "student";
    }
  };

  const persistSession = (s) => {
    if (s?.access_token && s?.refresh_token) {
      supabase.auth.setSession({ access_token: s.access_token, refresh_token: s.refresh_token }).catch(() => {});
    }
  };

  const userWithToken = session ? { ...session.user, access_token: session.access_token } : null;

  // Called after successful LOGIN — routes to dashboard
  const handleAuth = async (data) => {
    setActiveUser(data.user.id);
    setSession(data);
    persistSession(data);
    const role = await fetchRole(data.access_token, data.user.id);
    setView(roleToView(role));
  };

  // Called after successful SIGNUP — shows Welcome page
  const handleSignupSuccess = async (sess) => {
    persistSession(sess);
    const userId = sess.user.id;
    const token  = sess.access_token;
    let name = sess.user.user_metadata?.full_name?.split(" ")[0] || "there";
    let role = sess.user.user_metadata?.role || "student";
    try {
      const rows = await sbQuery("profiles", token, "GET", null, `?id=eq.${userId}&select=full_name,role`);
      if (rows?.[0]) {
        role = rows[0].role || role;
        name = rows[0].full_name?.split(" ")[0] || name;
      }
    } catch {}
    setWelcomeData({ name, role, session: sess });
    setView("welcome");
  };

  const handleProceedToDashboard = () => {
    setSession(welcomeData.session);
    setView(roleToView(welcomeData.role));
  };

  const handleLogout = () => {
    clearUserStorage();
    supabase.auth.signOut().catch(() => {});
    setSession(null);
    setWelcomeData(null);
    setView("landing");
  };

  // ── Resolve current page ──
  const renderView = () => {
    if (view === "welcome" && welcomeData) {
      return (
        <motion.div key="welcome" {...PAGE_TRANSITION}>
          <WelcomePage name={welcomeData.name} role={welcomeData.role} onProceed={handleProceedToDashboard} />
        </motion.div>
      );
    }
    if (view === "student-dash" && session) {
      return <motion.div key="student" {...PAGE_TRANSITION}><StudentDashboard user={userWithToken} onLogout={handleLogout} /></motion.div>;
    }
    if (view === "tutor-dash" && session) {
      return <motion.div key="tutor" {...PAGE_TRANSITION}><TutorDashboard user={userWithToken} onLogout={handleLogout} /></motion.div>;
    }
    if (view === "admin-dash" && session) {
      return <motion.div key="admin" {...PAGE_TRANSITION}><AdminDashboard user={userWithToken} token={session.access_token} onLogout={handleLogout} /></motion.div>;
    }
    if (view === "parent-dash" && session) {
      return <motion.div key="parent" {...PAGE_TRANSITION}><ParentDashboard user={userWithToken} onLogout={handleLogout} /></motion.div>;
    }
    if (view === "signup") {
      return (
        <motion.div key="signup" {...PAGE_TRANSITION}>
          <SignUpPage
            onSignIn={() => setView("signin")}
            onSignupSuccess={handleSignupSuccess}
            referralCode={refCode}
          />
        </motion.div>
      );
    }
    if (view === "signin") {
      return (
        <motion.div key="signin" {...PAGE_TRANSITION}>
          <SignInPage
            onAuth={handleAuth}
            onSignUp={() => setView("signup")}
          />
        </motion.div>
      );
    }
    // Default: landing page
    return (
      <motion.div key="landing" {...PAGE_TRANSITION}>
        <LandingPage
          onSignIn={() => setView("signin")}
          onSignUp={() => setView("signup")}
        />
      </motion.div>
    );
  };

  return (
    <>
      <CursorTrailLayer />
      <AnimatePresence>
        {showPreloader && <Preloader key="preloader" />}
      </AnimatePresence>
      {!initializing && (
        <Suspense fallback={null}>
          <AnimatePresence mode="wait">{renderView()}</AnimatePresence>
        </Suspense>
      )}
    </>
  );
}
